import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type CSSProperties,
} from 'react'
import { View } from 'react-native'

import Loader from './Loader'
import type { UniversalWebViewProps } from './types'

export default function UniversalIframe<MData>({ ref, ...props }: UniversalWebViewProps<MData>) {
  const {
    source,
    style,
    className,
    startInLoadingState = true,
    injectedJavaScript,
    onLoad,
    onError,
    onMessage,
    onContentReady,
    sandbox,
    allow,
    allowFullscreen = false,
  } = props

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [loading, setLoading] = useState(!!startInLoadingState)

  useImperativeHandle(ref, () => ({
    reload: () => {
      if (iframeRef.current) {
        // reload iframe by resetting src
        const src = iframeRef.current.src
        iframeRef.current.src = 'about:blank'
        // small timeout to ensure reload happens
        setTimeout(() => (iframeRef.current!.src = src), 10)
      }
    },
    postMessage: (message: string) => {
      try {
        iframeRef.current?.contentWindow?.postMessage(message, '*')
      } catch (e) {
        console.warn('[UniversalWebView] postMessage failed', e)
      }
    },
    goBack: () => {
      console.warn('[UniversalWebView] goBack is not implemented for web iframe')
    },
    goForward: () => {
      console.warn('[UniversalWebView] goForward is not implemented for web iframe')
    },
  }))

  // Message bridge for web (listen to messages from iframe)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // if iframe ref exists, ensure event source equals contentWindow to avoid cross-talk
      if (!iframeRef.current) return
      if (event.source !== iframeRef.current.contentWindow) return
      if (event.data?.event === 'contentReady') {
        onContentReady?.()
        return
      }
      if (onMessage) onMessage(event.data)
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [onMessage, onContentReady])

  const uri = (() => {
    if (!('uri' in source) || !source.uri) return undefined
    try {
      const url = new URL(source.uri)
      url.searchParams.set('embedded', 'nextapp')
      return url.toString()
    } catch {
      return source.uri
    }
  })()
  const html = 'html' in source ? source.html : undefined

  const handleLoad = useCallback(() => {
    setLoading(false)
    onLoad?.()

    // execute injectedJavaScript for iframe by posting a message that the iframe should run
    if (injectedJavaScript && iframeRef.current) {
      try {
        // try to evaluate in iframe by using postMessage; requires cooperating content
        iframeRef.current.contentWindow?.postMessage({ __runInjectedJS: injectedJavaScript }, '*')
      } catch {
        // ignore
      }
    }
  }, [injectedJavaScript, onLoad])

  const handleError = useCallback(
    (e: any) => {
      setLoading(false)
      onError?.(e)
    },
    [onError]
  )

  const iframeProps: ComponentPropsWithRef<'iframe'> = {
    ref: iframeRef,
    style: { flex: 1, borderWidth: 0, ...(style as CSSProperties) },
    sandbox,
    allow,
    allowFullScreen: allowFullscreen,
    onLoad: handleLoad,
    onError: handleError,
  }

  return (
    <View className={className} style={style}>
      {loading && <Loader />}
      {uri ? (
        // iframe with src
        <iframe {...iframeProps} src={uri} title={uri} />
      ) : html ? (
        // iframe with srcdoc
        <iframe {...iframeProps} srcDoc={html} title="document" />
      ) : null}
    </View>
  )
}
